import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarPlus, Music, Users, Check, ArrowRight, Loader2 } from "lucide-react";
import { api, apiError, APP_NAME } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const OPTIONS = [
  {
    type: "creator",
    title: "Criador de Eventos",
    description: "Quero criar eventos, contratar músicos e gerenciar minhas equipes.",
    icon: CalendarPlus,
    perks: ["Publicar cachês", "Analisar candidatos", "Escalar bandas"],
  },
  {
    type: "freelancer",
    title: "Músico Freelancer",
    description: "Quero encontrar eventos, receber convites e participar de oportunidades.",
    icon: Music,
    perks: ["Candidatar-se a gigs", "Aceitar convites", "Gerenciar cachês"],
  },
  {
    type: "both",
    title: "Ambos",
    description: "Crio eventos e também toco — quero tudo em um só painel.",
    icon: Users,
    perks: ["Criar e participar", "Painel unificado", "Alternar quando quiser"],
  },
];

export default function PersonaSelect() {
  const navigate = useNavigate();
  const { user, loadUser } = useAuth();
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/me/persona-suggestion")
      .then((r) => { if (alive && r.data?.suggested) setSelected((s) => s || r.data.suggested); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const firstName = (user?.stage_name || "").split(" ")[0] || "";
  const label = OPTIONS.find((o) => o.type === selected)?.title || "";

  const submit = async () => {
    if (!selected || saving) return;
    setSaving(true);
    try {
      await api.post("/me/persona", { user_type: selected });
      await loadUser();
      toast.success("Perfil configurado!");
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-10 overflow-x-hidden" data-testid="persona-select-page">
      <div className="pointer-events-none fixed -top-24 -right-24 h-72 w-72 rounded-full bg-primary/10 blur-3xl" aria-hidden="true" />
      <div className="w-full max-w-3xl relative">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-primary font-bold mb-3">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" /> {APP_NAME}
          </div>
          <h1 className="font-head text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
            {firstName ? `Boas-vindas, ${firstName}! ` : ""}Como você pretende usar o {APP_NAME}?
          </h1>
          <p className="text-muted-foreground mt-3 text-sm sm:text-base">
            Escolha o seu perfil para personalizarmos seu painel e suas ferramentas. Você pode mudar depois nas configurações.
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-4" role="radiogroup" aria-label="Escolha seu perfil de uso">
          {OPTIONS.map((o) => {
            const Icon = o.icon;
            const active = selected === o.type;
            return (
              <button
                key={o.type}
                type="button"
                role="radio"
                aria-checked={active}
                data-testid={`persona-card-${o.type}`}
                onClick={() => setSelected(o.type)}
                className={`group relative text-left rounded-3xl border p-6 min-h-[200px] transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-[0.99] ${
                  active
                    ? "border-primary bg-primary/10 ring-2 ring-primary/50 shadow-[0_0_30px_hsl(var(--primary)/0.18)]"
                    : "border-border bg-card hover:border-primary/40 hover:bg-surface-hover"
                }`}
              >
                <span className={`absolute top-4 right-4 flex h-7 w-7 items-center justify-center rounded-full border transition-colors ${active ? "bg-primary border-primary text-primary-foreground" : "border-border text-transparent"}`}>
                  <Check className="h-4 w-4" />
                </span>
                <span className={`flex h-14 w-14 items-center justify-center rounded-2xl mb-4 transition-colors ${active ? "bg-primary/20 text-primary" : "bg-white/5 text-primary"}`}>
                  <Icon className="h-7 w-7" />
                </span>
                <h2 className="font-head text-xl font-bold tracking-tight">{o.title}</h2>
                <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{o.description}</p>
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {o.perks.map((p) => (
                    <span key={p} className="text-[11px] px-2 py-1 rounded-full bg-white/5 text-muted-foreground border border-border">{p}</span>
                  ))}
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-8 flex justify-center">
          <Button
            data-testid="persona-continue-btn"
            onClick={submit}
            disabled={!selected || saving}
            size="lg"
            className="w-full sm:w-auto px-10 h-12 gap-2 font-bold shadow-[0_0_20px_hsl(var(--primary)/0.35)]"
          >
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
            {selected ? `Continuar como ${label}` : "Selecione um perfil"}
            {!saving && selected ? <ArrowRight className="h-5 w-5" /> : null}
          </Button>
        </div>
      </div>
    </div>
  );
}
